import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { authApi, ApiError } from '../services/authApi';
import '../styles/ForgotPassword.css';

const ForgotPassword: React.FC = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [emailSent, setEmailSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setError(t('forgotPassword.error.emptyEmail') || 'Please enter your email address');
      return;
    }

    // Basic email validation
    if (!email.includes('@')) {
      setError(t('forgotPassword.error.invalidEmail') || 'Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    setError('');
    setMessage('');

    try {
      const response = await authApi.forgotPassword({ email });
      setMessage(response.message);
      setEmailSent(true);
    } catch (error) {
      console.error('Forgot password failed:', error);
      
      if (error instanceof ApiError) {
        setError(error.message);
      } else {
        setError(t('forgotPassword.error.networkError') || 'An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleTryAgain = () => {
    setEmailSent(false);
    setMessage('');
    setError('');
    setEmail('');
  };

  if (emailSent) {
    return (
      <div className="forgot-password-container">
        <div className="forgot-password-form-wrapper">
          <div className="forgot-password-header">
            <div className="logo-bg">
              <img src="https://infrasec.se/wp-content/uploads/2024/09/Infrasec_lockup_01@4x.png" alt="Infrasec" className="logo" />
            </div>
            <h1>{t('forgotPassword.successTitle') || 'Check Your Email'}</h1>
          </div>
          
          <div className="success-message">
            <p>{message}</p>
            <div className="form-actions">
              <button 
                type="button" 
                className="secondary-button" 
                onClick={handleTryAgain}
              >
                {t('forgotPassword.sendAnother') || 'Send Another Email'}
              </button>
              <Link to="/login" className="login-button">
                {t('forgotPassword.backToLogin') || 'Back to Login'}
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="forgot-password-container">
      <div className="forgot-password-form-wrapper">
        <div className="forgot-password-header">
          <div className="logo-bg">
            <img src="https://infrasec.se/wp-content/uploads/2024/09/Infrasec_lockup_01@4x.png" alt="Infrasec" className="logo" />
          </div>
          <h1>{t('forgotPassword.title') || 'Forgot Password'}</h1>
          <p>{t('forgotPassword.subtitle') || 'Enter your email address and we\'ll send you a link to reset your password.'}</p>
        </div>
        
        {error && <div className="error-message">{error}</div>}
        
        <form className="forgot-password-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="email">{t('forgotPassword.email') || 'Email Address'}</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('forgotPassword.emailPrompt') || 'Enter your registered email address'}
              disabled={isLoading}
              autoFocus
            />
          </div>
          
          <div className="form-actions">
            <button 
              type="submit" 
              className="forgot-password-button" 
              disabled={isLoading}
            >
              {isLoading ? t('forgotPassword.sending') || 'Sending...' : t('forgotPassword.sendResetLink') || 'Send Reset Link'}
            </button>
          </div>
          
          <div className="form-footer">
            <Link to="/login" className="back-to-login">
              {t('forgotPassword.backToLogin') || 'Back to Login'}
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ForgotPassword;
